<template>
  <header class="app-header" :class="{ 'app-header--scrolled': isScrolled }">
    <div class="app-header__inner container">
      <!-- 左侧：Logo + 公司名称 -->
      <router-link to="/" class="app-header__brand">
        <img src="@/assets/logo.png" :alt="t('common.companyShort')" class="app-header__logo" />
        <span class="app-header__company">{{ t('common.companyShort') }}</span>
      </router-link>

      <!-- 中间：导航链接（桌面端） -->
      <nav class="app-header__nav">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="app-header__nav-link"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <!-- 右侧：语言切换 + 汉堡按钮 -->
      <div class="app-header__actions">
        <div class="app-header__lang-switch">
          <button
            class="lang-btn"
            :class="{ 'lang-btn--active': currentLang === 'zh-CN' }"
            @click="setLang('zh-CN')"
          >简</button>
          <button
            class="lang-btn"
            :class="{ 'lang-btn--active': currentLang === 'zh-TW' }"
            @click="setLang('zh-TW')"
          >繁</button>
        </div>
        <button
          class="app-header__hamburger"
          @click="mobileMenu.toggle"
          aria-label="菜单"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, inject } from 'vue'
import { state } from '@/i18n'

const t = inject('t')
const mobileMenu = inject('mobileMenu')

const currentLang = computed(() => state.currentLocale)

function setLang(lang) {
  state.currentLocale = lang
}

const isScrolled = ref(false)

const navItems = [
  { path: '/', labelKey: 'nav.home' },
  { path: '/about', labelKey: 'nav.about' },
  { path: '/strategy', labelKey: 'nav.strategy' },
  { path: '/risk-compliance', labelKey: 'nav.riskCompliance' },
  { path: '/cross-border', labelKey: 'nav.crossBorder' },
  { path: '/business', labelKey: 'nav.business' },
  { path: '/contact', labelKey: 'nav.contact' },
]

// 使用 computed 风格的响应式标签
navItems.forEach(item => {
  Object.defineProperty(item, 'label', {
    get() { return t(item.labelKey) }
  })
})

function handleScroll() {
  isScrolled.value = window.scrollY > 20
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-header);
  height: var(--header-height);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: box-shadow var(--transition-base), background var(--transition-base);
}

.app-header--scrolled {
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.98);
}

.app-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

/* 品牌 Logo */
.app-header__brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  text-decoration: none;
  flex-shrink: 0;
}

.app-header__logo {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.app-header__company {
  font-family: var(--font-serif);
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--color-text-primary);
  letter-spacing: 0.05em;
  white-space: nowrap;
}

/* 导航链接 */
.app-header__nav {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.app-header__nav-link {
  position: relative;
  padding: var(--space-2) var(--space-4);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 400;
  color: var(--color-text-secondary);
  text-decoration: none;
  letter-spacing: 0.03em;
  transition: color var(--transition-fast);
  white-space: nowrap;
}

.app-header__nav-link:hover {
  color: var(--color-text-primary);
}

.app-header__nav-link::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%) scaleX(0);
  width: calc(100% - var(--space-8));
  height: 2px;
  background: linear-gradient(90deg, var(--blue-dark), var(--blue-bright), var(--blue-dark));
  border-radius: 1px;
  transition: transform var(--transition-base);
}

.app-header__nav-link:hover::after,
.app-header__nav-link.router-link-active::after,
.app-header__nav-link.router-link-exact-active::after {
  transform: translateX(-50%) scaleX(1);
}

.app-header__nav-link.router-link-active,
.app-header__nav-link.router-link-exact-active {
  color: var(--blue);
}

/* 右侧操作�?*/
.app-header__actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-shrink: 0;
}

/* 语言切换按钮组 */
.app-header__lang-switch {
  display: flex;
  gap: 8px;
}

.lang-btn {
  padding: 4px 14px;
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.05em;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.lang-btn:hover {
  border-color: var(--blue-light);
  color: var(--blue);
}

.lang-btn--active {
  border-color: var(--blue);
  background: var(--blue-soft);
  color: var(--blue);
}

/* 汉堡菜单按钮 */
.app-header__hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  padding: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: opacity var(--transition-fast);
}

.app-header__hamburger:hover {
  opacity: 0.7;
}

.app-header__hamburger span {
  display: block;
  width: 20px;
  height: 2px;
  background: var(--color-text-primary);
  border-radius: 1px;
  transition: transform var(--transition-base), opacity var(--transition-base);
}

/* 响应�?*/
@media (max-width: 768px) {
  .app-header__nav {
    display: none;
  }

  .app-header__company {
    
  }

  .app-header__hamburger {
    display: flex;
  }

  .app-header__logo {
    height: 52px;
  }
}
</style>
